// Spell walls, in map squares. A thin wall runs along grid lines, in the same edge keys as the Map Maker's walls ("x,y,h"
// along the top of square x,y, "x,y,v" down its left side). A thick wall fills squares. Pure rules: placing one is
// server/utils/spellWalls.js, and drawing one is SpellAreaPreview.
import { segmentCrossesBox } from '../cover.mjs';
import { MAP_OBJECTS, WALL_KINDS, normalizeLayout } from '../../mapLayout.mjs';

const key = ({ x, y }) => `${x},${y}`;

/** Straight legs between clicked points: along the row, then down the column, as the drawing shows. */
export const pathLegs = (points = []) => {
  const legs = [];
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1], to = points[index], corner = { x: to.x, y: from.y };
    if (from.x !== to.x) legs.push([from, corner]);
    if (from.y !== to.y) legs.push([corner, to]);
  }
  return legs;
};
export const pathEdges = (points = []) => [...new Set(pathLegs(points).flatMap(([from, to]) => {
  const edges = [], dx = Math.sign(to.x - from.x), dy = Math.sign(to.y - from.y);
  for (let { x, y } = from; x !== to.x || y !== to.y; x += dx, y += dy) edges.push(dx ? `${Math.min(x, x + dx)},${y},h` : `${x},${Math.min(y, y + dy)},v`);
  return edges;
}))];
export const pathCells = (points = []) => {
  const cells = new Map(points.slice(0, 1).map(point => [key(point), { x: point.x, y: point.y }]));
  for (const [from, to] of pathLegs(points)) {
    const dx = Math.sign(to.x - from.x), dy = Math.sign(to.y - from.y);
    for (let { x, y } = from; x !== to.x || y !== to.y;) { x += dx; y += dy; cells.set(key({ x, y }), { x, y }); }
  }
  return [...cells.values()];
};
/** A ring, dome, globe or cage: the square of grid lines around a point, the given feet across. */
export const ringPoints = (center, feet) => {
  const half = Math.max(1, Math.round(feet / 10));
  const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1], [-1, -1]];
  return corners.map(([x, y]) => ({ x: center.x + x * half, y: center.y + y * half }));
};
export const pathFeet = (points, { thick = false } = {}) => (thick ? pathCells(points).length : pathEdges(points).length) * 5;

const edgeLine = edge => {
  const [x, y, direction] = edge.split(',');
  const from = { x: Number(x), y: Number(y) };
  return direction === 'h' ? [from, { x: from.x + 1, y: from.y }] : [from, { x: from.x, y: from.y + 1 }];
};
const crosses = (a, b, c, d) => {
  const turn = (p, q, r) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  // Half-open on the wall's own ends, so a line through a grid corner meets one edge of the wall instead of slipping between two.
  return (turn(a, b, c) > 0) !== (turn(a, b, d) > 0) && turn(c, d, a) * turn(c, d, b) < 0;
};
/** The squares touching a thin wall, either side of it. */
export const edgeCells = (edges = []) => [...new Map(edges.flatMap(edge => {
  const [x, y, direction] = edge.split(',');
  const cell = { x: Number(x), y: Number(y) };
  return [cell, direction === 'h' ? { x: cell.x, y: cell.y - 1 } : { x: cell.x - 1, y: cell.y }];
}).map(cell => [key(cell), cell])).values()];
/** The squares within `reach` squares of a thin wall, on one side (+1, -1) or, with no side, on both. */
export const wallBand = (points, reach, side = 0) => {
  const cells = new Map();
  const add = (x, y) => cells.set(`${x},${y}`, { x, y });
  for (const [from, to] of pathLegs(points)) {
    const dx = Math.sign(to.x - from.x), dy = Math.sign(to.y - from.y), facing = (dx || dy) * side;
    for (let { x, y } = from; x !== to.x || y !== to.y; x += dx, y += dy) {
      const across = dx ? Math.min(x, x + dx) : Math.min(y, y + dy), line = dx ? y : x;
      for (let step = 1; step <= reach; step += 1) {
        const before = line - step, after = line + step - 1;
        if (facing >= 0) { if (dx) add(across, before); else add(after, across); }
        if (facing <= 0) { if (dx) add(across, after); else add(before, across); }
      }
    }
  }
  return [...cells.values()];
};

export const spellWalls = (effects, mapId) => (effects || []).filter(effect => effect?.kind === 'spell-wall' && (!mapId || effect.placement?.mapId === mapId));
/** The edges that stop this creature moving: any solid wall that hasn't let it through. */
export const blockingEdges = (effects, mapId, moverId) => spellWalls(effects, mapId)
  .filter(wall => wall.blocksMovement && !(wall.passThrough || []).includes(String(moverId))).flatMap(wall => wall.edges || []);
export const slowingCells = (effects, mapId) => spellWalls(effects, mapId).filter(wall => wall.quadrupleCost).flatMap(wall => wall.cells || []);

const lineMeets = (wall, from, to) => (wall.edges || []).some(edge => { const [a, b] = edgeLine(edge); return crosses(from, to, a, b); })
  || (wall.cells || []).some(({ x, y }) => segmentCrossesBox(from, to, { x0: x + .05, y0: y + .05, x1: x + .95, y1: y + .95 }));
/**
 * What stops an area of effect: 2024 Total Cover between its point of origin and a square. Solid spell walls, and the map's
 * drawn walls and solid blocks when its layout shares the map's grid. Doors, windows and low walls let it through.
 */
export const areaBarriers = ({ effects, map, columns, rows }) => {
  const edges = spellWalls(effects, map?.mapId).filter(wall => wall.cover === 'total').flatMap(wall => wall.edges || []);
  const layout = map?.layout && typeof map.layout === 'object' ? normalizeLayout(map.layout) : null;
  if (!layout || layout.columns !== columns || layout.rows !== rows) return { edges, cells: [] };
  return { edges: [...edges, ...Object.entries(layout.walls || {}).filter(([, kind]) => WALL_KINDS[kind]?.cover === 'total' && WALL_KINDS[kind]?.blocksMovement).map(([edge]) => edge)],
    cells: Object.entries(layout.objects || {}).filter(([, id]) => MAP_OBJECTS[id]?.cover === 'total').map(([cell]) => { const [x, y] = cell.split(',').map(Number); return { x, y }; }) };
};
/** Whether a straight line from the origin reaches this point past the barriers. */
export const originReaches = (origin, point, barriers) => !(barriers?.edges || []).some(edge => { const [a, b] = edgeLine(edge); return crosses(origin, point, a, b); })
  && !(barriers?.cells || []).some(({ x, y }) => (Math.floor(point.x) !== x || Math.floor(point.y) !== y) && segmentCrossesBox(origin, point, { x0: x + .05, y0: y + .05, x1: x + .95, y1: y + .95 }));

/** The walls on the line between two points in map squares. */
export const wallsBetween = (effects, mapId, from, to) => spellWalls(effects, mapId).filter(wall => lineMeets(wall, from, to));
